/**
 * /operations/eos/gov-filings-queue — Session 3B PR 9.
 *
 * Owning agent: Mutaba'aa (gov-filing accuracy + Article 88 timeline).
 */

import { useQuery } from "@tanstack/react-query";
import { AlertOctagon, ClipboardCopy, FileText, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { GovFilingsQueueItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

type StateFilter =
  | "all"
  | "drafted"
  | "ready"
  | "submitted"
  | "accepted"
  | "rejected";

function _statusToneClass(s: GovFilingsQueueItem["status"]): string {
  if (s === "accepted") return "bg-emerald-100 text-emerald-800";
  if (s === "rejected") return "bg-rose-100 text-rose-800";
  if (s === "submitted") return "bg-brand-blue/15 text-brand-deep";
  if (s === "ready") return "bg-amber-100 text-amber-800";
  return "bg-slate-100 text-slate-700";
}

const _ARTICLE_88_THRESHOLD_DAYS = 7;

export function EosGovFilingsQueuePage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [stateFilter, setStateFilter] = useState<StateFilter>("all");

  const list = useQuery({
    queryKey: ["session3b-gov-filings", legalEntityId, stateFilter],
    queryFn: () =>
      session3bApi.listGovFilingsQueue(
        legalEntityId ?? "",
        stateFilter === "all" ? undefined : stateFilter,
      ),
    enabled: !!legalEntityId,
  });

  const items: GovFilingsQueueItem[] = list.data?.items ?? [];

  const counts = useMemo(() => {
    const overdue = items.filter(
      (r) =>
        (r.status === "drafted" || r.status === "ready") &&
        (r.days_since_drafted ?? 0) > _ARTICLE_88_THRESHOLD_DAYS,
    ).length;
    const drafted = items.filter((r) => r.status === "drafted" || r.status === "ready").length;
    const typeCounts = new Map<string, number>();
    for (const r of items) {
      typeCounts.set(r.filing_type, (typeCounts.get(r.filing_type) ?? 0) + 1);
    }
    const top = Array.from(typeCounts.entries()).sort((a, b) => b[1] - a[1])[0];
    return {
      total: items.length,
      attention: drafted,
      critical: overdue,
      top_type: top?.[0],
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("eos.gov_filings.title", "Government Filings Queue")}
          subtitle={t(
            "eos.gov_filings.subtitle",
            "Qiwa termination · GOSI exit · Iqama exit drafts. Mutaba'aa watches the Article 88 timeline. Live submission via M3X.GOV; today: manual portal copy.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(["all", "drafted", "ready", "submitted", "accepted", "rejected"] as StateFilter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setStateFilter(f)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  stateFilter === f
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`eos.gov_filings.filter.${f}`, f)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "eos.gov_filings.cta.bulk_draft_note",
                  "Bulk-draft creates Qiwa + GOSI + (expat → Iqama) drafts for every active termination missing them.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {t("eos.gov_filings.cta.bulk_draft", "Draft All Pending Filings Now")}
          </button>
        </div>

        {counts.critical > 0 && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<AlertOctagon className="h-4 w-4" />}
            severity="critical"
            headline={t(
              "eos.gov_filings.suggestion.overdue_headline",
              `${counts.critical} filing${counts.critical === 1 ? "" : "s"} drafted but not submitted >7 days · Article 88 violation imminent.`,
            )}
            body={t(
              "eos.gov_filings.suggestion.overdue_body",
              "Mutaba'aa note: until M3X.GOV ships live API submission, HR must copy the JSON to the Qiwa/GOSI/Muqeem portals manually. Use the row-level 'Copy' action.",
            )}
            primaryAction={{
              label: t("eos.gov_filings.suggestion.show_drafted", "Show drafted only"),
              onClick: () => setStateFilter("drafted"),
            }}
            suggestionKey="filings-overdue"
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<FileText className="h-4 w-4" />}
            severity="info"
            headline={t(
              "eos.gov_filings.suggestion.empty_headline",
              "No filings in the queue.",
            )}
            body={t(
              "eos.gov_filings.suggestion.empty_body",
              "Mutaba'aa notes: filings are seeded when a termination workflow advances past 'settlement_calculated'. Trigger 'Draft All Pending Filings Now' if active terminations have none.",
            )}
            primaryAction={{
              label: t("eos.gov_filings.suggestion.open_eos", "Active separations"),
              onClick: () => window.location.assign("/operations/eos"),
            }}
            suggestionKey="filings-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("eos.gov_filings.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("eos.gov_filings.col.type", "Filing type")}</th>
                  <th className="px-3 py-2">{t("eos.gov_filings.col.drafted", "Drafted")}</th>
                  <th className="px-3 py-2">{t("eos.gov_filings.col.status", "Status")}</th>
                  <th className="px-3 py-2">{t("eos.gov_filings.col.days", "Days since draft")}</th>
                  <th className="px-3 py-2">{t("eos.gov_filings.col.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.filing_id} className="group border-t border-border-subtle hover:bg-surface-sunken/40">
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-xs uppercase text-text-secondary">{row.filing_type}</td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span dir="ltr">{row.drafted_at ?? "—"}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_statusToneClass(row.status)}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "eos.gov_filings.trace.days",
                              `days_since_drafted = today − drafted_at. Article 88 sets the gov-filing window for termination; Mutaba'aa flags critical when status='drafted'/'ready' and days_since > 7.`,
                            ),
                          )
                        }
                      >
                        <span className="font-mono text-xs">
                          {row.days_since_drafted ?? "—"}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 opacity-50 transition-opacity group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() =>
                            showAlertTrace(
                              t(
                                "eos.gov_filings.action.copy_note",
                                "Copy filing JSON to clipboard. HR pastes it into the Qiwa/GOSI/Muqeem portal manually until M3X.GOV ships live submission.",
                              ),
                            )
                          }
                          className="rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] hover:bg-brand-blue/10"
                        >
                          <ClipboardCopy className="me-1 inline h-3 w-3" />
                          {t("eos.gov_filings.action.copy", "Copy JSON")}
                        </button>
                        {(row.status === "drafted" || row.status === "ready") && (
                          <button
                            type="button"
                            onClick={() =>
                              showAlertTrace(
                                t(
                                  "eos.gov_filings.action.mark_submitted_note",
                                  "Mark this filing as submitted manually — sets submitted_at = now and status='submitted'.",
                                ),
                              )
                            }
                            className="rounded border border-emerald-300 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50"
                          >
                            {t("eos.gov_filings.action.mark_submitted", "Mark submitted")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="mutabaa"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: counts.critical,
            top_kind: t("eos.gov_filings.title", "Gov Filings Queue"),
            window_label: counts.top_type
              ? `top type: ${counts.top_type}`
              : t("eos.gov_filings.window_label_empty", "no filings"),
          }}
        />
      </aside>
    </div>
  );
}
